import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type IndexationBody =
  paths["/api/v1/contracts/{contract_id}/indexations"]["post"]["requestBody"]["content"]["application/json"];

const idxKey = (c: string) => ["contracts", c, "indexations"] as const;

export function useIndexations(contractId: string) {
  return useQuery({
    queryKey: idxKey(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/contracts/{contract_id}/indexations",
        { params: { path: { contract_id: contractId } } },
      );
      if (error || !data) throw new Error("Failed to load indexations");
      return data;
    },
  });
}

export function useCreateIndexation(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IndexationBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/indexations",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to apply indexation");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: idxKey(contractId) });
      qc.invalidateQueries({ queryKey: ["contracts"] });
    },
  });
}
